import * as React from "react";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Table, Button, Input, Modal, Alert, Form, Typography, Spin } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';

import EntityInfo from "../types/EntityInfo";
import { deleteProduct, fetchProducts, postProduct } from "../api/product";
import { operationNames, userHasAccess } from "../data/pages";
import { useTexts } from "../hooks/useTexts";

const { Title, Text } = Typography;

const Products = () => {
    const { t } = useTexts();
    const [data, setData] = useState<{ products: EntityInfo[] }>({ products: [] });
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState<string[]>([]);
    const [open, setOpen] = useState(false);
    const [rowToDelete, setRowToDelete] = useState<EntityInfo>({ id: 0, name: '' });
    const [form] = Form.useForm();

    const getProducts = () => {
        setLoading(true);
        fetchProducts().then(response => {
            setData({ products: response?.items || [] });
        })
        .catch(error => console.error("ERROR", error))
        .finally(() => setLoading(false));
    }

    const openModal = () => setOpen(true);
    const closeModal = () => setOpen(false);

    const removeProduct = (id: number) => {
        deleteProduct(id).then(() => {
            closeModal();
            getProducts();
        })
        .catch(error => console.error("ERROR", error));
    }

    useEffect(() => {
        getProducts();
    }, []);

    const handleFinish = (values: { name: string }): void => {
        setErrors([]);
        setSubmitting(true);
        postProduct({ name: values.name })
            .then(response => {
                if (response?.id) {
                    getProducts();
                    toast.success(t('product', 'productAdded'), { theme: "colored" });
                    form.resetFields();
                } else if (!!response?.messages?.length && response?.messages?.length > 0) {
                    setErrors(response.messages);
                }
            })
            .catch(error => console.error("ERROR", error))
            .finally(() => setSubmitting(false));
    };

    const columns: ColumnsType<EntityInfo> = [
        {
            title: '#',
            key: 'index',
            render: (text, record, index) => index + 1,
            width: 60,
        },
        {
            title: t('product', 'productName'),
            dataIndex: 'name',
            key: 'name',
        }
    ];

    if (userHasAccess(operationNames.DELETE_PRODUCT)) {
        columns.push({
            title: '',
            key: 'action',
            width: 120,
            render: (_, record) => (
                <Button 
                    danger 
                    type="primary" 
                    icon={<DeleteOutlined />}
                    onClick={() => {
                        setRowToDelete(record);
                        openModal();
                    }}
                >
                    {t('common', 'delete')}
                </Button>
            )
        });
    }

    return (
        <div style={{ marginTop: '50px' }}>
            <Modal
                title={`'${rowToDelete?.name}' ${t('product', 'deleteConfirm')}`}
                open={open}
                onCancel={() => {
                    setRowToDelete({ id: 0, name: '' });
                    closeModal();
                }}
                onOk={() => removeProduct(rowToDelete?.id)}
                okText={t('common', 'yes')}
                cancelText={t('common', 'close')}
                okButtonProps={{ danger: true }}
            >
                <Text>Are you sure you want to delete '{rowToDelete?.name}'?</Text>
            </Modal>

            {errors?.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                    {errors.map((err, idx) => (
                        <Alert key={idx} message={err} type="error" showIcon style={{ marginBottom: '8px' }} />
                    ))}
                </div>
            )}

            <Form
                form={form}
                layout="inline"
                onFinish={handleFinish}
                style={{ marginBottom: '24px' }}
            >
                <Form.Item
                    name="name"
                    rules={[{ required: true, message: 'Please enter a product name' }]}
                >
                    <Input placeholder={t('product', 'productName')} />
                </Form.Item>
                <Form.Item>
                    <Button type="primary" htmlType="submit" loading={submitting} icon={<PlusOutlined />}>
                        {t('product', 'addProduct')}
                    </Button>
                </Form.Item>
            </Form>

            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: "50px" }}>
                    <Spin size="large" />
                </div>
            ) : data.products?.length > 0 ? (
                <Table 
                    columns={columns} 
                    dataSource={data.products} 
                    rowKey="id" 
                    pagination={{ pageSize: 10 }}
                    style={{ background: 'white', padding: '16px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}
                />
            ) : (
                <Title level={4}>{t('product', 'productNotFound')}</Title>
            )}
        </div>
    )
}

export default Products;